<template>
  <DialogBase
    :visible.sync="visible"
    :title="dialogTitle"
    size="lg"
    :confirm-loading="loading"
    confirm-text="Lưu nhận xét"
    cancel-text="Đóng"
    :use-panel="true"
    :panel-title="panelTitle"
    :panel-subtitle="`Nhập cùng nội dung cho nhiều học sinh`"
    :panel-icon="period === 'WEEKLY' ? 'el-icon-document' : 'el-icon-notebook-2'"
    @confirm="handleSave"
    @cancel="handleClose"
  >
    <el-form
      ref="dataForm"
      :model="dataForm"
      :rules="formRules"
      label-position="top"
      class="sk-u-mt-12"
    >
      <!-- BỘ CHỌN HỌC SINH TÙY CHỌN LINH HOẠT -->
      <div class="selected-students-card">
        <div class="selected-students-card__header">
          <div class="selected-students-card__title">
            <i class="el-icon-user" /> Danh sách học sinh nhận xét
            <el-tag
              size="mini"
              :type="dataForm.students.length > 0 ? 'primary' : 'danger'"
              class="ml-2"
            >
              Đã chọn: {{ dataForm.students.length }} / {{ allStudents.length }} học sinh
            </el-tag>
          </div>
          <div class="selected-students-card__actions">
            <el-button
              size="mini"
              type="text"
              icon="el-icon-check"
              @click="selectAllStudents"
            >
              Chọn tất cả
            </el-button>
            <el-button
              size="mini"
              type="text"
              icon="el-icon-close"
              class="text-danger"
              @click="deselectAllStudents"
            >
              Bỏ chọn hết
            </el-button>
          </div>
        </div>

        <div class="selected-students-picker">
          <el-select
            v-model="selectedStudentIds"
            multiple
            filterable
            collapse-tags
            placeholder="Tìm kiếm và tích chọn học sinh cần nhận xét..."
            style="width: 100%"
            @change="onStudentSelectionChange"
          >
            <el-option
              v-for="student in allStudents"
              :key="student.studentId"
              :label="student.studentName"
              :value="student.studentId"
            />
          </el-select>
        </div>

        <div v-if="dataForm.students.length > 0" class="selected-students">
          <el-tag
            v-for="student in dataForm.students"
            :key="student.studentId"
            closable
            size="small"
            class="selected-student-tag"
            @close="removeStudent(student.studentId)"
          >
            {{ student.studentName }}
          </el-tag>
        </div>
        <div v-else class="selected-students-empty">
          <i class="el-icon-warning-outline" /> Chưa có học sinh nào được chọn. Vui lòng chọn học sinh ở ô trên!
        </div>
      </div>

      <el-form-item :label="period === 'WEEKLY' ? 'Tuần' : 'Tháng'" prop="date">
        <el-date-picker
          v-model="dataForm.date"
          :type="period === 'WEEKLY' ? 'week' : 'month'"
          :value-format="period === 'WEEKLY' ? 'yyyy-MM-dd' : 'yyyy-MM'"
          :format="period === 'WEEKLY' ? 'Tuần WW (từ dd/MM/yyyy)' : 'MM/yyyy'"
          :placeholder="period === 'WEEKLY' ? 'Chọn tuần' : 'Chọn tháng'"
          :style="{ width: period === 'WEEKLY' ? '100%' : '200px' }"
          :clearable="false"
          disabled
        />
      </el-form-item>

      <el-form-item label="Nội dung" prop="content">
        <el-input
          v-model="dataForm.content"
          type="textarea"
          :rows="6"
          placeholder="Nhập nội dung nhận xét..."
          :maxlength="contentMaxlength"
          show-word-limit
        />
      </el-form-item>

      <!-- File/ảnh đính kèm (Ở DƯỚI CÙNG SAU CÁC NHẬN XÉT) -->
      <div class="evl-upload-section">
        <div class="evl-upload-section__header">
          <span class="evl-upload-title">
            <i class="el-icon-paperclip" /> File & hình ảnh đính kèm
          </span>
          <span class="evl-upload-subtitle">Tối đa 3 file/nhận xét (dùng chung cho các học sinh đã chọn)</span>
        </div>
        <el-upload
          drag
          action="#"
          :auto-upload="false"
          :file-list="fileList"
          :limit="3"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          multiple
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :on-exceed="handleFileExceed"
          class="evl-drag-uploader"
        >
          <i class="el-icon-upload" />
          <div class="el-upload__text">
            Kéo thả file/ảnh vào đây hoặc <em>nhấn để chọn file</em>
          </div>
        </el-upload>
      </div>
    </el-form>
  </DialogBase>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import EvaluationService from "@/modules/evaluation/services/EvaluationService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import moment from "moment";
import { EVALUATION_FILE_MAX_COUNT, isEvaluationFileAllowed } from "@/modules/evaluation/utils/evaluationFile";

export default {
  name: "EvaluationPeriodBatchFormDialog",
  components: { DialogBase },
  props: {
    period: { type: String, required: true },
    date: { type: String, default: "" },
  },
  data() {
    return {
      visible: false,
      loading: false,
      allStudents: [],
      selectedStudentIds: [],
      dataForm: {
        students: [],
        date: "",
        content: "",
      },
      fileList: [],
      formRules: {
        date: [{ required: true, message: "Vui lòng chọn thời gian", trigger: "change" }],
        content: [{ required: true, message: "Vui lòng nhập nội dung", trigger: "blur" }],
      },
    };
  },
  computed: {
    isWeekly() {
      return this.period === "WEEKLY";
    },
    dialogTitle() {
      return `Tạo nhận xét ${this.isWeekly ? "tuần" : "tháng"} cho nhiều học sinh`;
    },
    panelTitle() {
      return `Nhận xét ${this.isWeekly ? "tuần" : "tháng"} chung`;
    },
    contentMaxlength() {
      return this.isWeekly ? 500 : 1000;
    },
  },
  methods: {
    openForCreate(preselectedStudents = [], allClassStudents = []) {
      this._resetForm();

      const sourceAll = allClassStudents && allClassStudents.length > 0
        ? allClassStudents
        : preselectedStudents;

      this.allStudents = (sourceAll || []).map(student => ({
        studentId: student.studentId,
        studentName: student.studentName,
      }));

      const initialSelected = preselectedStudents && preselectedStudents.length > 0
        ? preselectedStudents
        : this.allStudents;

      this.selectedStudentIds = initialSelected.map(s => s.studentId);
      this._syncStudentsFromIds();

      this.dataForm.date = this.isWeekly
        ? (this.date || moment().startOf("isoWeek").format("YYYY-MM-DD"))
        : (this.date ? this.date.substring(0, 7) : moment().format("YYYY-MM"));
      this.visible = true;
    },

    onStudentSelectionChange(ids) {
      this.selectedStudentIds = ids;
      this._syncStudentsFromIds();
    },

    _syncStudentsFromIds() {
      this.dataForm.students = this.allStudents.filter(s =>
        this.selectedStudentIds.includes(s.studentId)
      );
    },

    selectAllStudents() {
      this.selectedStudentIds = this.allStudents.map(s => s.studentId);
      this._syncStudentsFromIds();
    },

    deselectAllStudents() {
      this.selectedStudentIds = [];
      this._syncStudentsFromIds();
    },

    removeStudent(studentId) {
      this.selectedStudentIds = this.selectedStudentIds.filter(id => id !== studentId);
      this._syncStudentsFromIds();
    },

    async handleSave() {
      const valid = await this.$refs.dataForm.validate().catch(() => false);
      if (!valid) return;
      if (this.dataForm.students.length === 0) {
        NotifyMessage.leftCornerNotificationError("Vui lòng chọn ít nhất 1 học sinh nhận xét!");
        return;
      }

      this.loading = true;
      try {
        const request = {
          date: this.dataForm.date,
          students: this.dataForm.students,
          content: this.dataForm.content.trim(),
          files: this.getFiles(),
        };
        if (this.isWeekly) {
          await EvaluationService.createWeeklyBatchCommon(request);
        } else {
          await EvaluationService.createMonthlyBatchCommon(request);
        }
        NotifyMessage.leftCornerNotificationSuccess(
          `Tạo nhận xét ${this.isWeekly ? "tuần" : "tháng"} cho ${this.dataForm.students.length} học sinh thành công!`
        );
        this.$emit("saved");
        this.handleClose();
      } catch (error) {
        NotifyMessage.leftCornerNotificationError(
          error?.response?.data?.message || "Tạo nhận xét thất bại!"
        );
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$refs.dataForm?.resetFields();
      this.visible = false;
      this._resetForm();
    },

    handleFileChange(file, fileList) {
      if (!isEvaluationFileAllowed(file.raw)) {
        NotifyMessage.leftCornerNotificationError("Chỉ được tải ảnh, file Word, Excel hoặc PDF!");
        this.fileList = fileList.filter(item => item.uid !== file.uid);
        return;
      }
      if (fileList.length > EVALUATION_FILE_MAX_COUNT) {
        NotifyMessage.leftCornerNotificationError("Mỗi nhận xét chỉ được đính kèm tối đa 3 file!");
        this.fileList = fileList.slice(0, EVALUATION_FILE_MAX_COUNT);
        return;
      }
      this.fileList = fileList;
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList;
    },

    handleFileExceed() {
      NotifyMessage.leftCornerNotificationError("Mỗi nhận xét chỉ được đính kèm tối đa 3 file!");
    },

    getFiles() {
      return this.fileList.map(file => file.raw).filter(Boolean);
    },

    _resetForm() {
      this.dataForm = { students: [], date: "", content: "" };
      this.allStudents = [];
      this.selectedStudentIds = [];
      this.fileList = [];
      this.$nextTick(() => this.$refs.dataForm?.clearValidate());
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog";

.selected-students-card {
  margin: 4px 0 14px;
  padding: 12px 14px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #eff6ff;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 13.5px;
    font-weight: 700;
    color: #1e40af;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__actions {
    display: flex;
    gap: 8px;

    .text-danger {
      color: #ef4444;
      &:hover {
        color: #dc2626;
      }
    }
  }
}

.selected-students-picker {
  margin-bottom: 10px;
}

.selected-students {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 110px;
  overflow-y: auto;
  padding: 4px 0;
}

.selected-student-tag {
  background: #ffffff;
  border-color: #bfdbfe;
  color: #1e40af;
  border-radius: 6px;
  font-weight: 500;
}

.selected-students-empty {
  font-size: 12.5px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
}

/* ─── Modern Drag Dropzone ───────────────────────── */
.evl-upload-section {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
}

.evl-upload-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.evl-upload-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    color: #2563eb;
    font-size: 15px;
  }
}

.evl-upload-subtitle {
  font-size: 12px;
  color: #94a3b8;
}

.evl-drag-uploader {
  width: 100%;

  /deep/ .el-upload {
    width: 100%;
  }

  /deep/ .el-upload-dragger {
    width: 100%;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
    transition: all 0.2s ease;

    &:hover {
      border-color: #3b82f6;
      background: #eff6ff;
    }

    .el-icon-upload {
      font-size: 32px;
      color: #3b82f6;
      margin: 0 0 2px;
      line-height: 1;
    }

    .el-upload__text {
      font-size: 13px;
      color: #64748b;
      line-height: 1.4;

      em {
        color: #2563eb;
        font-weight: 600;
        font-style: normal;
      }
    }
  }

  /deep/ .el-upload-list {
    margin-top: 8px;

    .el-upload-list__item {
      border-radius: 8px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      margin-top: 6px;
      transition: all 0.15s ease;

      &:hover {
        background: #e2e8f0;
      }
    }
  }
}

.ml-2 {
  margin-left: 8px;
}
</style>
